/**
 * Closing CTA — premium glass waitlist card (AI-11994 rebuild).
 * Oversized heading kept from the template, but the action is now an
 * inline form: name (optional) + email posting straight to the production
 * Worker waitlist endpoint (CORS verified — the Worker echoes
 * access-control-allow-origin for jesusenteredthechat.com).
 */
import { type FormEvent, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";

const WAITLIST_ENDPOINT = "https://jesus-entered-the-chat-api.julianb233.workers.dev/api/waitlist";
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type Status =
  | { state: "idle" }
  | { state: "submitting" }
  | { state: "success" }
  | { state: "error"; message: string };

export function ClosingCta() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<Status>({ state: "idle" });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const cleanEmail = email.trim().toLowerCase();
    if (!EMAIL_PATTERN.test(cleanEmail)) {
      setStatus({ state: "error", message: "Enter a valid email so we can send your TestFlight invite." });
      return;
    }

    setStatus({ state: "submitting" });
    try {
      const response = await fetch(WAITLIST_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          email: cleanEmail,
          focus: "testflight",
          source: "landing-screenshots",
        }),
      });
      const body: { ok?: boolean; error?: unknown } = await response.json().catch(() => ({}));

      if (response.ok && body?.ok) {
        setStatus({ state: "success" });
        return;
      }
      setStatus({
        state: "error",
        message:
          typeof body?.error === "string"
            ? body.error
            : "We couldn't save that just now. Try again, or use the full waitlist page.",
      });
    } catch {
      setStatus({
        state: "error",
        message: "We couldn't reach the waitlist right now. Try again, or use the full waitlist page.",
      });
    }
  };

  return (
    <section className="relative overflow-hidden bg-sky-fade-up px-6 py-28 text-center md:px-12 md:py-40">
      {/* Ambient glow behind the glass card */}
      <div
        aria-hidden
        className="pointer-events-none absolute bottom-0 left-1/2 h-[480px] w-[860px] -translate-x-1/2 bg-sky-glow blur-3xl"
      />

      <div className="relative">
        <h2 className="mx-auto max-w-4xl text-[11vw] font-medium leading-[0.95] tracking-tighter text-neutral-900 md:text-7xl">
          Join the private launch list.
        </h2>
        <p className="mx-auto mt-8 max-w-xl text-lg leading-relaxed text-neutral-500">
          Get instructions for mobile access, Fruit sprints, church challenge pilots, and the first
          Christian community cohort.
        </p>

        <div className="mx-auto mt-14 w-full max-w-xl rounded-[32px] border border-white/80 bg-white/70 p-7 text-left shadow-glass backdrop-blur-xl md:p-9">
          {status.state === "success" ? (
            <div className="flex flex-col items-center py-4 text-center">
              <div className="grid h-14 w-14 place-items-center rounded-full bg-cta-gradient text-white shadow-cta">
                <CheckCircle2 size={26} />
              </div>
              <h3 className="mt-6 text-2xl font-semibold tracking-tight text-[#121823]">You're in!</h3>
              <p className="mt-3 max-w-sm text-base leading-7 text-neutral-600">
                We saved your request. We&apos;ll email you when your selected access is ready.
              </p>
              <Link
                to="/storyboard"
                className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-[#0064D2] transition hover:text-[#004fa6]"
              >
                Preview the app screens
                <ArrowRight size={15} />
              </Link>
            </div>
          ) : (
            <form onSubmit={(event) => void handleSubmit(event)} className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="block">
                  <span className="text-sm font-semibold text-[#121823]">Name (optional)</span>
                  <input
                    value={name}
                    onChange={(event) => {
                      setName(event.target.value);
                      if (status.state === "error") setStatus({ state: "idle" });
                    }}
                    className="mt-2 h-12 w-full rounded-2xl border border-[#cfdff2] bg-white/90 px-4 text-sm outline-none transition focus:border-[#007AFF] focus:ring-4 focus:ring-[#007AFF]/12"
                    placeholder="Your name"
                    autoComplete="name"
                  />
                </label>
                <label className="block">
                  <span className="text-sm font-semibold text-[#121823]">Email</span>
                  <input
                    value={email}
                    onChange={(event) => {
                      setEmail(event.target.value);
                      if (status.state === "error") setStatus({ state: "idle" });
                    }}
                    className="mt-2 h-12 w-full rounded-2xl border border-[#cfdff2] bg-white/90 px-4 text-sm outline-none transition focus:border-[#007AFF] focus:ring-4 focus:ring-[#007AFF]/12"
                    placeholder="you@example.com"
                    type="email"
                    autoComplete="email"
                    required
                  />
                </label>
              </div>

              {status.state === "error" ? (
                <div className="rounded-2xl border border-[#f0c9a6] bg-[#fff7ec] px-4 py-3 text-sm leading-6 text-[#8a5a1e]">
                  {status.message}{" "}
                  <Link to="/waitlist" className="font-semibold underline">
                    Open the full waitlist form
                  </Link>
                  .
                </div>
              ) : null}

              <button
                type="submit"
                disabled={status.state === "submitting"}
                className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-full bg-cta-gradient px-8 py-3.5 text-sm font-semibold text-white shadow-cta transition hover:brightness-110 active:translate-y-px active:shadow-cta-pressed disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto"
              >
                {status.state === "submitting" ? "Joining…" : "Join the waitlist"}
                <ArrowRight size={16} />
              </button>
              <p className="text-xs leading-5 text-neutral-500">
                TestFlight invitations are sent after Apple approves external testing. Prefer to tell us more?{" "}
                <Link to="/waitlist" className="font-semibold text-[#0064D2] transition hover:text-[#004fa6]">
                  Use the full form
                </Link>
                .
              </p>
            </form>
          )}
        </div>

        <div className="mt-10 flex items-center justify-center">
          <Link
            to="/storyboard"
            className="rounded-full border border-[#bcd2ec] bg-white/60 px-8 py-4 text-sm font-medium text-neutral-900 backdrop-blur-sm transition hover:border-[#5b8fd6]"
          >
            Preview screens
          </Link>
        </div>
      </div>
    </section>
  );
}
